import React, { useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import {
  Boxes,
  Building2,
  Calculator,
  CalendarDays,
  CalendarRange,
  Factory,
  Mail,
  Menu,
  Package,
  ScrollText,
  Settings as SettingsIcon,
  Shield,
  Store,
  Table2,
  Tags,
  Timer,
  Users,
} from "lucide-react";
import TabbedPage from "../../../../shared/ui/TabbedPage";
import { LoadingBlock } from "../../../../shared/ui/composites";
import StaffTab from "../../../staff/components/StaffTab";
import WorkingCalendarPage from "../../../working-calendar/pages/WorkingCalendarPage";
import SettingEditor from "../../components/SettingEditor";
import {
  useGetSettings,
  useGetVisibleSettings,
} from "../../api/react-query";

const TAB_META = {
  staff: {
    label: "پرسنل",
    icon: <Users className="h-3.5 w-3.5" />,
    group: "عملیات",
  },
  "working-calendar": {
    label: "روزهای کاری شمسی",
    icon: <CalendarDays className="h-3.5 w-3.5" />,
    group: "عملیات",
  },
  "working-calendar-gregorian": {
    label: "روزهای کاری میلادی",
    icon: <CalendarRange className="h-3.5 w-3.5" />,
    group: "عملیات",
  },
  job: {
    icon: <Shield className="h-3.5 w-3.5" />,
    group: "پیکربندی",
  },
  sidemenu: {
    icon: <Menu className="h-3.5 w-3.5" />,
    group: "پیکربندی",
  },
  general: {
    icon: <SettingsIcon className="h-3.5 w-3.5" />,
    group: "پیکربندی",
  },
  targetgroups: {
    icon: <Package className="h-3.5 w-3.5" />,
    group: "کالا و برند",
  },
  goodsgroup: {
    icon: <Tags className="h-3.5 w-3.5" />,
    group: "کالا و برند",
  },
  manufacturer: {
    icon: <Factory className="h-3.5 w-3.5" />,
    group: "کالا و برند",
  },
  tasks: {
    icon: <Timer className="h-3.5 w-3.5" />,
    group: "اعلان‌ها",
  },
  providers: {
    icon: <Mail className="h-3.5 w-3.5" />,
    group: "اعلان‌ها",
  },
  commission_formula: {
    icon: <Calculator className="h-3.5 w-3.5" />,
    group: "پورسانت",
  },
  commission_rules: {
    icon: <ScrollText className="h-3.5 w-3.5" />,
    group: "پورسانت",
  },
  coefficient_tables: {
    icon: <Table2 className="h-3.5 w-3.5" />,
    group: "پورسانت",
  },
  commission_calc_formula: {
    icon: <Calculator className="h-3.5 w-3.5" />,
    group: "پورسانت",
  },
  commission_indicators: {
    icon: <Calculator className="h-3.5 w-3.5" />,
    group: "پورسانت",
  },
  dc: {
    icon: <Building2 className="h-3.5 w-3.5" />,
    group: "پیکربندی",
  },
  saleoffice: {
    icon: <Store className="h-3.5 w-3.5" />,
    group: "پیکربندی",
  },
  product_options: {
    icon: <Boxes className="h-3.5 w-3.5" />,
    group: "کالا و برند",
  },
};

function iconForKey(key) {
  return TAB_META[key]?.icon || <SettingsIcon className="h-3.5 w-3.5" />;
}

export default function SettingsPage() {
  const [searchParams] = useSearchParams();
  const {
    data: visibleSettings = [],
    isLoading: visibleLoading,
    error: visibleError,
  } = useGetVisibleSettings();
  const { data: allSettings = [], isLoading: allLoading } = useGetSettings();

  const settingsCatalog = allSettings.length ? allSettings : visibleSettings;

  const tabs = useMemo(() => {
    const dynamicTabs = (visibleSettings || []).map((setting) => {
      const latest =
        settingsCatalog.find((row) => row.settings_key === setting.settings_key) ||
        setting;
      const meta = TAB_META[setting.settings_key] || {};

      return {
        id: setting.settings_key,
        label: setting.title || setting.settings_key,
        icon: iconForKey(setting.settings_key),
        group: meta.group || "پیکربندی",
        render: () => (
          <SettingEditor setting={latest} allSettings={settingsCatalog} />
        ),
      };
    });

    return [
      {
        id: "staff",
        label: TAB_META.staff.label,
        icon: TAB_META.staff.icon,
        group: TAB_META.staff.group,
        component: StaffTab,
      },
      {
        id: "working-calendar",
        label: TAB_META["working-calendar"].label,
        icon: TAB_META["working-calendar"].icon,
        group: TAB_META["working-calendar"].group,
        render: () => <WorkingCalendarPage calendarType="jalali" />,
      },
      {
        id: "working-calendar-gregorian",
        label: TAB_META["working-calendar-gregorian"].label,
        icon: TAB_META["working-calendar-gregorian"].icon,
        group: TAB_META["working-calendar-gregorian"].group,
        render: () => <WorkingCalendarPage calendarType="gregorian" />,
      },
      ...dynamicTabs,
    ];
  }, [visibleSettings, settingsCatalog]);

  if (visibleLoading || allLoading) {
    return <LoadingBlock message="در حال بارگذاری تنظیمات..." />;
  }

  if (visibleError) {
    return (
      <div className="p-6 text-destructive">
        {visibleError.message || "خطا در دریافت تنظیمات"}
      </div>
    );
  }

  const requestedTab = searchParams.get("tab");
  const defaultTab =
    requestedTab && tabs.some((tab) => tab.id === requestedTab)
      ? requestedTab
      : "staff";

  return (
    <TabbedPage
      title="تنظیمات"
      subtitle="پرسنل، تقویم کاری و پیکربندی سامانه را از نوار کناری انتخاب کنید."
      defaultTab={defaultTab}
      orientation="vertical"
      tabs={tabs}
      placeholderText="برای این تنظیم هنوز ویرایشگری تعریف نشده است"
    />
  );
}
